import React, { ReactNode, useState } from 'react';
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette } from '@qualifio/ui-commons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';

interface ChildProps {
  language: string;
}

interface Props {
  children: (props: ChildProps) => ReactNode;
  languages: string[];
  message?: ReactNode;
  errors?: Record<string, unknown>;
}

export function LanguageTabs({ children, message, errors, languages }: Props): React.ReactElement {
  const [language, setLanguage] = useState(languages[0]);

  const showTabs = languages.length > 1;
  const hasError = (lang: string) => errors?.[lang] !== undefined;

  return (
    <div>
      <Box sx={{ alignItems: 'center', display: 'flex' }}>
        {message && (
          <Typography sx={{ marginBottom: 1 }} variant="body1">
            {message}
          </Typography>
        )}
        {languages.length > 1 && (
          <Tabs
            sx={{ marginLeft: 'auto', height: 35, minHeight: 0 }}
            value={language}
            onChange={(_, value) => setLanguage(value)}
          >
            {languages.map(lang => (
              <Tab
                key={lang}
                sx={{
                  minHeight: 0,
                  minWidth: 35,
                  height: 34,
                  paddingX: 1,
                  paddingY: 0,
                  '&.Mui-selected': {
                    backgroundColor: colorPalette.grey.light,
                  },
                }}
                label={
                  <>
                    <Box
                      component="img"
                      sx={{ width: 20, height: 20 }}
                      src={`${BASE_PATH}/assets/template/assets/flags/${lang}.svg`}
                      alt={lang}
                    />
                    {hasError(lang) && (
                      <Box
                        sx={{
                          color: ({ palette }) => palette.error.main,
                          marginLeft: 0.5,
                        }}
                      >
                        <FontAwesomeIcon icon={faExclamationCircle} />
                      </Box>
                    )}
                  </>
                }
                value={lang}
              />
            ))}
          </Tabs>
        )}
      </Box>
      <Paper
        sx={{
          backgroundColor: colorPalette.grey.light,
          borderTopRightRadius: showTabs ? 0 : undefined,
          padding: 2,
        }}
      >
        {children({ language })}
      </Paper>
    </div>
  );
}
